var _ = require('optimizely-client-fn');
var actionTypes = require('core/constants/action_types');
var di = require('core/di');
var DirectiveStore = di.get('stores/directive');
var DocumentEnv = require('core/env/document');
var MutationObserverEnv = require('core/env/mutation_observer');
var enums = require('core/constants/enums');
var Flux = require('core/flux');
var generateUniqueId = require('optimizely-uuid').generate;
var poll = require('./poll');
var WindowEnv = require('core/env/window');

var RumStore = di.get('stores/rum');

// Default options for the observeSelector option parameter
var DEFAULT_OPTIONS = {
  once: false,
  onTimeout: null,
  timeout: null,
};

var _observers = {} as any;

/**
 * Method used to initialize observation of a selector.
 * This is only invoked once and overwrites itself with the
 * result of the initialization function.
 * @param id
 */
var initializeObservation:Function = function(id){
  if (__TEST__) {
    // Always execute the setup of the observation method when testing
    // since different test cases may require different observation methods.
    initializeObservationMethod()(id);
  } else {
    // Overwrite the module-level initializeObservation function to avoid redundant setup.
    initializeObservation = initializeObservationMethod();
    initializeObservation(id);
  }
};

/**
 * Return an observation method to be used.
 * @return {Function}
 */
// TODO: Refactor to use core/observer
function initializeObservationMethod(): Function {
  // Indefinite change observation should be done with the MutationObserver API
  // TODO (james): Once we feel MutationObservers should be used for all customers
  //               (regardless of the timeout setting), update this check to more simply
  //               be DirectiveStore.mutationObserverAPISupported()
  if (DirectiveStore.shouldObserveChangesIndefinitely()) {
    var MUTATION_OBSERVER_CONFIG = {
      attributes: true,
      childList: true,
      subtree: true,  // This is expensive, but required
      characterData: true
    };

    var MUTATION_OBSERVER_TARGET = DocumentEnv.getDocumentElement();

    /**
     * Observe changes to the document.
     *    See https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
     * @type {MutationObserver}
     */
    // TODO(james): Investigate performance benefits of debouncing this (at least during page load)
    var observer;
    function callback() {
      this.disconnect();  // Disconnect before triggering potential side effects of matchSelector.
      _.each(_.keys(_observers), matchSelector);
      this.observe(MUTATION_OBSERVER_TARGET, MUTATION_OBSERVER_CONFIG);  // Reconnect when done.

      if (__RUM_DOM_OBSERVATION_COUNTERS__ && RumStore.getSampleRum()) {
        // Record that we reacted do a Document Mutation Event
        Flux.dispatch(actionTypes.RECORD_DOM_OBSERVATION_OCCURENCE, {
          counterName: enums.PerformanceCounters.mutation_observer_invocation
        });
      }
    }

    if (!__EXPERIMENTAL_SHADOW_DOM__) {
      observer = new MutationObserver(callback);
    } else {
      observer = new MutationObserverEnv.ShadowDomMutationObserver(callback);
    }

    return function(id) {
      var thisObserver = _observers[id];
      // Multiple observations of the same element have no effect
      observer.observe(MUTATION_OBSERVER_TARGET, MUTATION_OBSERVER_CONFIG);

      thisObserver.cancelObservation = function () {
        delete _observers[id];

        // Stop observation if we no longer have any selectors to observe.
        // The next observation will connect again as needed.
        if (_.isEmpty(_observers)) {
          observer.disconnect();
        }
      };
    };

  // Not observing indefinitely, so use the default polling mechanism instead.
  } else {
    return function(id) {
      var cancelPolling = poll.poll(_.partial(matchSelector, id));
      // When polling, unobserving the selector should cancel polling and delete the observer.
      _observers[id].cancelObservation = function() {
        cancelPolling();
        delete _observers[id];
      };
    };
  }
}

/**
 * Invoke the supplied callback function whenever the supplied selector
 * matches an element in the DOM. Callback function will be supplied a single
 * HTMLDomElement as its only parameter. By default, the callback will be
 * invoked once for each element returned by the selector.
 *
 * @param {string} selector (CSS selector)
 * @param {function} callback
 * @param {object} options
 * @param {number|function} [options.timeout] Number of milliseconds, or a predicate function, or null. Default to null (ie no timeout).
 * @param {boolean} options.once Only invoke the callback on the first match
 * @param {function} options.onTimeout If timeout is specified and no elements match, execute timeout callback
 * @returns {function} Invoke to unobserve selector
 */
exports.observeSelector = function(selector:string, callback:Function, options:any): Function {
  if (!validateQuerySelector(selector)) {
    throw new Error('observeSelector expects a valid css selector as its first argument');
  }

  if (!_.isFunction(callback)) {
    throw new Error('observeSelector expects a function as its second argument');
  }

  if (options && (!_.isObject(options) || _.isFunction(options))) {
    throw new Error('observeSelector expects an object as its third argument');
  }

  var id = generateUniqueId();
  options = _.assign({}, DEFAULT_OPTIONS, options || {});

  // Instantiate observer object
  _observers[id] = {
    callback: callback,
    callbackQueue: [],
    matchedCount: 0,
    options: options,
    selector: selector,
    startTime: Date.now(),
  };

  initializeObservation(id);

  // cede execution so we can return something (and so that we are consistent in cases that
  // element is present/not present)
  WindowEnv.setTimeout(_.bind(matchSelector, null, id), 0);

  // return a function that will unobserve all actions when invoked
  return _.partial(unobserveSelector, id);
};

/**
 * Unobserving a selector cancels the polling interval, and deletes itself from the observer object.
 *
 * @private
 * @param {string} id Unique identifier for an observer object
 */
function unobserveSelector(id:string) {
  var thisObserver = _observers[id];
  if (thisObserver && thisObserver.cancelObservation) {
    thisObserver.cancelObservation();
  }
}

/**
 * Checks to see if there are elements matching the supplied observers selector. If there are matches,
 * and they have not been matched before, we add each to the callback queue and invoke the executeCallbackQueue
 * function.
 *
 * @private
 * @param {string} id Unique identifier for an observer object
 */
function matchSelector(id:string) {
  if (_observers[id]) {
    if (isTimedOut(_observers[id])) {
      if (_observers[id].matchedCount === 0 && _.isFunction(_observers[id].options.onTimeout)) {
        _observers[id].options.onTimeout();
      }
      unobserveSelector(id);
      return;
    }
    var nodeList = DocumentEnv.querySelectorAll(_observers[id].selector);
    if (nodeList.length) {
      _.each(nodeList, function(node) {
        if (!node.__optimizelyObserveSelectorIds || !node.__optimizelyObserveSelectorIds[id]) {
          _observers[id].callbackQueue.push(node);
        }
      });
      executeCallbackQueue(id);
    }
    if (__RUM_DOM_OBSERVATION_COUNTERS__ && RumStore.getSampleRum()) {
      // Record that we invoked match_selector and querySelectorAll
      Flux.dispatch(actionTypes.RECORD_DOM_OBSERVATION_OCCURENCE, {
        counterName: enums.PerformanceCounters.match_selector_invocation
      });
    }
  }
}

/**
 * For a given observer, check if the timeout period has elapsed or if the timeout
 * function returns true.
 *
 * A number or a function can be passed as the observer.options.timeout
 *
 * @private
 * @param observer
 * @return {boolean|unknown}
 */
function isTimedOut(observer): boolean|unknown {
  var timeout = observer.options.timeout;
  if (timeout !== null) {
    if (typeof timeout === 'function') {
      try {
        return timeout();
      } catch(error) {
        // No op. - The users function may error initially, so just return false.
      }
    } else if (Date.now() - observer.startTime > timeout) {
      return true;
    }
  }
  return false;
}

/**
 * Go through the callback queue and invoke the observer callback with each of the queued nodes
 * Note: callback functions may invoke unobserveSelector, or depending on options we may do it ourselves.
 * So need to be careful to not loop over a null reference.
 *
 * @private
 * @param {string} id Unique identifier for an observer object
 */
function executeCallbackQueue(id:string) {
  while (_observers[id] && _observers[id].callbackQueue.length) {
    var node = _observers[id].callbackQueue.shift();
    markNode(node, id);
    _observers[id].matchedCount = _observers[id].matchedCount + 1;
    _observers[id].callback(node);
    if (_observers[id] && _observers[id].options.once) {
      unobserveSelector(id);
      return;
    }
  }
}

/**
 * Mark a dom node as having been matched with regards to a observer. Marked nodes will not be
 * passed to the callback again.
 *
 * @private
 * @param {HTMLElement} node
 * @param {string} id Unique identifier for an observer object
 */
function markNode(node:HTMLElement, id:string) {
  if (!node['__optimizelyObserveSelectorIds']) {
    node['__optimizelyObserveSelectorIds'] = {} as any;
  }
  node['__optimizelyObserveSelectorIds'][id] = true;
}

/**
 * Test that a given CSS selector is valid
 *
 * @private
 * @param {string} selector
 * @returns {boolean}
 */
function validateQuerySelector(selector:string): boolean {
  try {
    DocumentEnv.querySelector(selector);
  } catch (e) {
    return false;
  }
  return true;
}
